/**
 * The app's one toast, on top of pleach-ds Toast/ToastStack (D-13).
 *
 *   const toast = useToast();
 *   toast({ message: 'Saved Fish & chips at 29.8% food cost.' });
 *   toast({ message: 'Beef Burger moved to Trash.', undo: () => restore() });
 *   toast({ tone: 'error', message: 'Could not save. The server did not answer.' });
 *
 * Older call sites pass `title` (and sometimes `body`). They keep working and
 * render as one sentence, the way the handoff sets every toast: "Could not
 * delete" + "Butter is used in 2 recipes" reads "Could not delete. Butter is
 * used in 2 recipes."
 *
 * How the slot behaves:
 * - One toast at a time, bottom centre, ink. A new toast replaces the one on
 *   screen, with one exception: an error keeps the slot for its first 3s. A
 *   non-error that arrives in those 3s waits (the latest one wins) and shows
 *   when they are up. A newer error replaces at once.
 * - It goes after 5s, errors after 8s (`timeout` overrides; 0 keeps it until
 *   the next toast replaces it). The pleach-ds Toast runs the timer, so it
 *   pauses while the pointer is over the toast or focus is in it, and resumes
 *   with what was left (WCAG 2.2.1).
 * - One action at most: `undo` (labelled "Undo") or `onAction` with
 *   `actionLabel`. Pressing it closes the toast and runs the callback once,
 *   however fast the double click. F8 moves focus to it from anywhere.
 * - Errors are announced through ToastStack's `role="alert"` lane, everything
 *   else through its polite status lane.
 * - On a phone the toast sits above the tab bar (76px up). At any width it
 *   also clears the sticky SaveBar, or any other bottom bar marked
 *   `data-toast-clear`, when that bar would be under it, so it never covers
 *   the Save button it is reporting on.
 *
 * Colour: 'default' and 'success' look the same (ink). Green, amber and red
 * belong to money state; `estimate` adds the amber triangle when what was
 * saved still costs as an estimate.
 *
 * Code outside React (query cache handlers, live updates) can call
 * `showToast(input)`, which goes to the mounted provider.
 */
import { Toast, ToastStack } from 'pleach-ds';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useIsPhone } from '@/components/internal/useMediaQuery';

type ToastCommon = {
  /** 'default' for almost everything. 'error' is announced at once
   *  (role="alert"), shows for 8s and holds the slot for its first 3s.
   *  'success' renders exactly like 'default'. */
  tone?: 'default' | 'success' | 'error';
  /** What was saved still costs as an estimate: adds the amber triangle. */
  estimate?: boolean;
  /** ms before the toast goes. Default 5000, errors 8000. 0 keeps it until
   *  the next toast replaces it (there is no close button, so use sparingly). */
  timeout?: number;
  /** Shows an "Undo" button that runs this once and closes the toast. */
  undo?: () => unknown;
  /** The one action, when it is not Undo. Needs `actionLabel`. */
  onAction?: () => unknown;
  /** Label for `undo` or `onAction`. Defaults to "Undo". */
  actionLabel?: string;
  /** @deprecated A trailing control rendered as-is. Use `undo` or
   *  `actionLabel` + `onAction`; ignored when either is set. */
  action?: ReactNode;
};

export type ToastInput = ToastCommon &
  (
    | {
        /** The whole toast as one plain sentence. Prefer this in new code. */
        message: ReactNode;
        title?: never;
        body?: never;
      }
    | {
        /** Older shape: rendered as the start of the sentence. */
        title: ReactNode;
        /** Older shape: rendered after `title` as the rest of the sentence. */
        body?: ReactNode;
        message?: never;
      }
  );

type Shown = { id: number; input: ToastInput; at: number };

/** An error keeps the slot this long before a non-error may replace it (D-13). */
export const ERROR_HOLD_MS = 3000;

const isError = (t: ToastInput) => t.tone === 'error';

/** The single slot, outside React so the rules above are plain code. */
function createSlot() {
  let current: Shown | null = null;
  let waiting: ToastInput | null = null;
  let hold: ReturnType<typeof setTimeout> | undefined;
  let next = 1;
  const listeners = new Set<() => void>();

  const set = (input: ToastInput | null) => {
    current = input ? { id: next++, input, at: Date.now() } : null;
    listeners.forEach((l) => l());
  };

  // Let a waiting non-error in once the error on screen has had its 3s.
  const release = () => {
    clearTimeout(hold);
    hold = undefined;
    if (!waiting) return;
    const left = current && isError(current.input) ? current.at + ERROR_HOLD_MS - Date.now() : 0;
    if (left > 0) {
      hold = setTimeout(release, left);
      return;
    }
    const input = waiting;
    waiting = null;
    set(input);
  };

  return {
    get: (): Shown | null => current,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    push(input: ToastInput) {
      if (!isError(input)) {
        const held = current !== null && isError(current.input) && Date.now() - current.at < ERROR_HOLD_MS;
        if (held) {
          waiting = input; // the latest one wins the wait
          release();
          return;
        }
        waiting = null;
        clearTimeout(hold);
        set(input);
        return;
      }
      set(input);
      // Anything already waiting now waits out this newer error's 3s.
      release();
    },
    dismiss(id: number) {
      if (current?.id !== id) return;
      set(null);
      release();
    },
    /** The toast's button: close it, then run its callback exactly once. */
    act(id: number) {
      if (current?.id !== id) return;
      const run = current.input.undo ?? current.input.onAction;
      set(null);
      release();
      if (!run) return;
      const failed = (e: unknown) => console.error('toast action failed', e);
      try {
        const r = run();
        if (r && typeof (r as Promise<unknown>).then === 'function') (r as Promise<unknown>).then(undefined, failed);
      } catch (e) {
        failed(e);
      }
    },
    dispose() {
      clearTimeout(hold);
      hold = undefined;
    },
  };
}

type Slot = ReturnType<typeof createSlot>;

/** Ends a plain-text clause with a full stop unless it already has one. */
function stop(s: string): string {
  const t = s.trim();
  return !t || /[.!?…:]$/.test(t) ? t : `${t}.`;
}

/** The older title/body pair as one sentence; `message` passes through. */
function sentence(t: ToastInput): ReactNode {
  if (t.message != null) return t.message;
  const { title, body } = t;
  const head = typeof title === 'string' ? stop(title) : title;
  const hasBody = body != null && body !== false && body !== '';
  if (!hasBody) return head;
  const tail = typeof body === 'string' ? stop(body) : body;
  if (head === '' || head == null || head === false) return tail;
  if (typeof head === 'string' && typeof tail === 'string') return `${head} ${tail}`;
  return (
    <>
      {head} {tail}
    </>
  );
}

/** DS TabBar root class. The shell keeps it mounted and hides it from 768px
 *  up, so it only counts on a phone. */
const TAB_BAR = '.pl-tabbar';
/** Other bottom chrome the toast must not cover: anything marked
 *  `data-toast-clear`, plus the app's sticky SaveBar (`.savebar`, see
 *  components/SaveBar.tsx), which is known here by name so every editor that
 *  adopts it is covered without a second step. */
const CLEAR = '[data-toast-clear], .savebar';
/** The gap the toast keeps above a bar it clears (--pl-s-6). */
const GAP = 16;
/** Where the toast's bottom edge sits with no lift: 24px (--pl-s-8), or the
 *  tab bar plus 16px on a phone (DS ToastStack `offset`). */
const BASE = 24;
const TAB_BAR_H = 60;
/** A one-line toast; used when the stack cannot be measured yet. */
const MIN_TOAST_H = 40;

/**
 * How far up (px from the viewport bottom) the toast has to sit to clear a
 * marked bar that would otherwise be under it, or 0 when nothing is in the
 * way. Only a bar that actually overlaps the toast's usual spot counts: a
 * sticky bar still in the flow further up a short page is left alone, so the
 * toast does not float up the middle of the screen for nothing.
 */
function liftFor(tabBar: boolean): number {
  const vh = window.innerHeight;
  let base = BASE;
  if (tabBar) {
    const h = document.querySelector(TAB_BAR)?.getBoundingClientRect().height ?? 0;
    base = (h > 0 ? h : TAB_BAR_H) + GAP;
  }
  const stack = document.querySelector('.pl-toasts')?.getBoundingClientRect().height ?? 0;
  const bottom = vh - base; // the toast's bottom edge (viewport y)
  const top = bottom - Math.max(stack, MIN_TOAST_H);
  let highest = Infinity;
  document.querySelectorAll(CLEAR).forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.top < bottom && r.bottom > top) highest = Math.min(highest, r.top);
  });
  return highest === Infinity ? 0 : Math.round(vh - highest);
}

type Placement = { tabBar: boolean; lift: number };

/** Where the toast sits. On a phone with the tab bar it clears it (DS
 *  `offset="tabbar"`, 76px up); public pages have none and keep 24px, as
 *  desktop does. At any width, a marked bar such as the SaveBar that would be
 *  under the toast lifts it to 16px above that bar, so a toast never covers
 *  the Save button it is reporting on. */
function usePlacement(shownId: number | null) {
  const phone = useIsPhone();
  const [place, setPlace] = useState<Placement>({ tabBar: false, lift: 0 });
  // Layout effect, re-run per toast: its first frame is already in place.
  useLayoutEffect(() => {
    if (shownId === null) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      const tabBar = phone && document.querySelector(TAB_BAR) !== null;
      const lift = liftFor(tabBar);
      setPlace((p) => (p.tabBar === tabBar && p.lift === lift ? p : { tabBar, lift }));
    };
    const soon = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    // Watch only while a toast is up: bars come and go (log out, a form
    // turning dirty, a sticky bar scrolling into place).
    const mo = new MutationObserver(soon);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    window.addEventListener('resize', soon);
    window.addEventListener('scroll', soon, { passive: true });
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', soon);
      window.removeEventListener('scroll', soon);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [shownId, phone]);
  return {
    offset: phone && place.tabBar ? ('tabbar' as const) : ('default' as const),
    style: place.lift > 0 ? { bottom: `calc(${place.lift}px + var(--pl-s-6))` } : undefined,
  };
}

const Ctx = createContext<(t: ToastInput) => void>(() => {});

let mounted: Slot | null = null;

/** Show a toast from outside React. Returns false when no provider is mounted. */
export function showToast(input: ToastInput): boolean {
  if (!mounted) return false;
  mounted.push(input);
  return true;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const ref = useRef<Slot | null>(null);
  if (ref.current === null) ref.current = createSlot();
  const slot = ref.current;

  const shown = useSyncExternalStore(slot.subscribe, slot.get, slot.get);
  const place = usePlacement(shown ? shown.id : null);

  useEffect(() => {
    mounted = slot;
    return () => {
      if (mounted === slot) mounted = null;
      slot.dispose();
    };
  }, [slot]);

  const t = shown?.input;
  const run = t ? (t.undo ?? t.onAction) : undefined;

  return (
    <Ctx.Provider value={slot.push}>
      {children}
      <ToastStack offset={place.offset} style={place.style}>
        {shown && t ? (
          <Toast
            key={shown.id}
            tone={t.tone}
            estimate={t.estimate}
            message={sentence(t)}
            actionLabel={run ? (t.actionLabel ?? 'Undo') : undefined}
            onAction={run ? () => slot.act(shown.id) : undefined}
            action={run ? undefined : t.action}
            duration={t.timeout}
            onTimeout={() => slot.dismiss(shown.id)}
          />
        ) : null}
      </ToastStack>
    </Ctx.Provider>
  );
}

/** Returns a stable function that shows a toast. See the file header. */
export function useToast() {
  return useContext(Ctx);
}
